import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthProvider';
import {
  Area, Atividade, CicloFechamento, Dependencia, Empresa, Escopo, GrupoEconomico, GrupoEmpresas,
  ProdutoIntegrado, UsuarioMaestro,
} from '../models';
import { Feriado } from '../models/ciclo';
import { AcaoPDCA, DestaqueCiclo, PontuacaoArea, RecomendacaoInteligencia } from '../models/inteligencia';
import maestroService from '../maestroService';

export type VisaoMaestro =
  | 'executiva' | 'calendario' | 'linha-do-tempo' | 'gantt'
  | 'hierarquia' | 'area' | 'responsavel' | 'empresa'
  | 'dependencias' | 'caminho-critico' | 'pdca' | 'ranking'
  | 'riscos' | 'delegacao';

export type AbaAtividadeDrawer =
  | 'resumo' | 'dependencias' | 'checklist' | 'evidencias' | 'excecoes'
  | 'comunicacao' | 'historico' | 'inteligencia' | 'produto';

export interface IndicadoresMaestro {
  totalAtividades: number;
  concluidas: number;
  emAndamento: number;
  naoIniciadas: number;
  aguardandoDependencia: number;
  aguardandoEvidencia: number;
  atrasadas: number;
  progressoRealizado: number;
}

interface MaestroContextValue {
  carregando: boolean;
  erro: string | null;
  semDadosCadastrados: boolean;

  escopo: Escopo;
  setEscopo: (escopo: Escopo) => void;
  grupoEconomico: GrupoEconomico;
  gruposEconomicos: GrupoEconomico[];
  gruposEmpresas: GrupoEmpresas[];
  empresas: Empresa[];
  empresasEscopo: Empresa[];
  areas: Area[];
  usuarios: UsuarioMaestro[];
  feriados: Feriado[];

  cicloAtivo: CicloFechamento;
  ciclosAnteriores: CicloFechamento[];
  setCicloAtivoId: (id: number) => void;
  /** MAESTRO-RN-39 — pendências reais do checklist de encerramento (vazio quando pronto). */
  pendenciasEncerramento: string[];

  atividades: Atividade[];
  dependencias: Dependencia[];
  produtosIntegrados: ProdutoIntegrado[];
  recomendacoes: RecomendacaoInteligencia[];
  acoesPDCA: AcaoPDCA[];
  pontuacoes: PontuacaoArea[];
  destaques: DestaqueCiclo[];
  indicadores: IndicadoresMaestro;

  visaoAtiva: VisaoMaestro;
  setVisaoAtiva: (visao: VisaoMaestro) => void;

  atividadeSelecionadaId: number | null;
  abaDrawerInicial: AbaAtividadeDrawer;
  abrirAtividade: (id: number, aba?: AbaAtividadeDrawer) => void;
  fecharAtividade: () => void;

  getArea: (id?: number) => Area | undefined;
  getEmpresa: (id?: number) => Empresa | undefined;
  getUsuario: (id?: number) => UsuarioMaestro | undefined;

  recarregar: () => void;
}

const CICLO_VAZIO: CicloFechamento = {
  id: 0, competencia: '—', empresaIds: [], dataAbertura: '', prazoOficial: '', previsaoConclusao: '',
  status: 'NAO_INICIADO', progressoEsperado: 0, progressoRealizado: 0, desvio: 0, diasUteisRestantes: 0,
  responsavelAberturaId: 0, ultimaAtualizacao: '',
};

const GRUPO_ECONOMICO_VAZIO: GrupoEconomico = { id: 0, nome: 'Nenhum grupo econômico cadastrado', grupoEmpresasIds: [], responsavelId: 0 };

const MaestroContext = createContext<MaestroContextValue | undefined>(undefined);

const empresaIdsDoEscopo = (escopo: Escopo, empresas: Empresa[], gruposEmpresas: GrupoEmpresas[]): number[] => {
  if (escopo.nivel === 'EMPRESA' && escopo.empresaId) return [escopo.empresaId];
  if (escopo.nivel === 'GRUPO_EMPRESAS' && escopo.grupoEmpresasId) {
    const grupo = gruposEmpresas.find(g => g.id === escopo.grupoEmpresasId);
    return grupo?.empresaIds ?? [];
  }
  return empresas.filter(e => e.grupoEconomicoId === escopo.grupoEconomicoId).map(e => e.id);
};

/** MAESTRO — "Inteligência" não tem motor próprio no backend (seção 9.37 do handoff): recomendações
 * genéricas, porém reais, derivadas de atividades atrasadas/aguardando dependência do ciclo ativo. */
const derivarRecomendacoes = (atividades: Atividade[]): RecomendacaoInteligencia[] => {
  const recomendacoes: RecomendacaoInteligencia[] = [];

  atividades.filter(a => a.status === 'ATRASADA').forEach(a => recomendacoes.push({
    id: a.id * 10 + 1,
    tipo: 'RISCO_ATRASO',
    titulo: `Atividade atrasada: ${a.nome}`,
    motivo: `A atividade "${a.nome}" está com status ATRASADA no ciclo atual.`,
    areaId: a.areaId,
    responsavelId: a.responsavelId,
    atividadeIds: [a.id],
    impacto: 'Pode comprometer o prazo do ciclo de fechamento.',
    acaoSugerida: 'Acionar o responsável e reavaliar o prazo previsto.',
    status: 'NOVA',
  }));

  atividades.filter(a => a.status === 'AGUARDANDO_DEPENDENCIA').forEach(a => recomendacoes.push({
    id: a.id * 10 + 2,
    tipo: 'DEPENDENCIA_PENDENTE',
    titulo: `Aguardando dependência: ${a.nome}`,
    motivo: `A atividade "${a.nome}" está bloqueada aguardando a conclusão de uma dependência.`,
    areaId: a.areaId,
    responsavelId: a.responsavelId,
    atividadeIds: [a.id],
    impacto: 'Atraso na dependência propaga atraso para esta atividade.',
    acaoSugerida: 'Verificar o andamento da dependência de origem.',
    status: 'NOVA',
  }));

  // MAESTRO-RN-26 (gap de auditoria) — AGUARDANDO_EVIDENCIA agora é status persistido (RN-29): a
  // tentativa de concluir sem evidência validada transiciona a atividade para cá em vez de só
  // lançar uma exceção. Mesmo padrão de derivação usado acima para AGUARDANDO_DEPENDENCIA.
  atividades.filter(a => a.status === 'AGUARDANDO_EVIDENCIA').forEach(a => recomendacoes.push({
    id: a.id * 10 + 3,
    tipo: 'EVIDENCIA_AUSENTE',
    titulo: `Aguardando evidência: ${a.nome}`,
    motivo: `A atividade "${a.nome}" não pôde ser concluída: falta anexar e validar a evidência obrigatória.`,
    areaId: a.areaId,
    responsavelId: a.responsavelId,
    atividadeIds: [a.id],
    impacto: 'A atividade permanece pendente até a evidência ser anexada e validada.',
    acaoSugerida: 'Anexar a evidência obrigatória e solicitar a validação.',
    status: 'NOVA',
  }));

  return recomendacoes;
};

const derivarDestaques = (pontuacoes: PontuacaoArea[]): DestaqueCiclo[] =>
  [...pontuacoes]
    .sort((a, b) => b.pontuacaoTotal - a.pontuacaoTotal)
    .slice(0, 3)
    .map(p => ({ titulo: `Destaque — pontuação ${p.pontuacaoTotal}`, responsavelId: p.id, descricao: `Posição ${p.posicaoRanking} no ranking do ciclo.` }));

export const MaestroProvider: React.FC = ({ children }) => {
  const { usuario } = useAuth();

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [gruposEconomicos, setGruposEconomicos] = useState<GrupoEconomico[]>([]);
  const [gruposEmpresas, setGruposEmpresas] = useState<GrupoEmpresas[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioMaestro[]>([]);
  const [feriados, setFeriados] = useState<Feriado[]>([]);
  const [ciclos, setCiclos] = useState<CicloFechamento[]>([]);
  const [cicloAtivoId, setCicloAtivoId] = useState<number | null>(null);

  const [atividades, setAtividades] = useState<Atividade[]>([]);
  const [dependencias, setDependencias] = useState<Dependencia[]>([]);
  const [acoesPDCA, setAcoesPDCA] = useState<AcaoPDCA[]>([]);
  const [pontuacoes, setPontuacoes] = useState<PontuacaoArea[]>([]);
  const [prontoParaEncerrar, setProntoParaEncerrar] = useState(false);
  const [pendenciasEncerramento, setPendenciasEncerramento] = useState<string[]>([]);
  const [produtosIntegrados, setProdutosIntegrados] = useState<ProdutoIntegrado[]>([]);

  const [escopo, setEscopo] = useState<Escopo>({ nivel: 'GRUPO_ECONOMICO', grupoEconomicoId: 0 });
  const [visaoAtiva, setVisaoAtiva] = useState<VisaoMaestro>('executiva');
  const [atividadeSelecionadaId, setAtividadeSelecionadaId] = useState<number | null>(null);
  const [abaDrawerInicial, setAbaDrawerInicial] = useState<AbaAtividadeDrawer>('resumo');
  const [recarregarToken, setRecarregarToken] = useState(0);

  // Dados de configuração (escopo/áreas/usuários/feriados/ciclos) — carregados uma vez por sessão do cliente logado.
  useEffect(() => {
    let cancelado = false;

    async function carregar() {
      setCarregando(true);
      setErro(null);
      try {
        const [economicos, deEmpresas] = await Promise.all([
          maestroService.listarGruposEconomicos(),
          maestroService.listarGruposEmpresas(),
        ]);
        if (cancelado) return;

        const primeiroGrupo = economicos[0];
        const [listaEmpresas, listaAreas, listaFeriados, listaCiclos] = await Promise.all([
          primeiroGrupo ? maestroService.listarEmpresas(primeiroGrupo.id, deEmpresas) : Promise.resolve([]),
          maestroService.listarAreas(),
          maestroService.listarFeriados(),
          maestroService.listarCiclos(),
        ]);
        if (cancelado) return;

        const listaUsuarios = usuario?.clienteID ? await maestroService.listarUsuarios() : [];
        if (cancelado) return;

        setGruposEconomicos(economicos);
        setGruposEmpresas(deEmpresas);
        setEmpresas(listaEmpresas);
        setAreas(listaAreas);
        setFeriados(listaFeriados);
        setUsuarios(listaUsuarios);
        setCiclos(listaCiclos);

        if (primeiroGrupo) {
          setEscopo(prev => (prev.grupoEconomicoId ? prev : { nivel: 'GRUPO_ECONOMICO', grupoEconomicoId: primeiroGrupo.id }));
        }

        const cicloEmAndamento = listaCiclos.find(c => c.status !== 'ENCERRADO') ?? listaCiclos[0];
        if (cicloEmAndamento) setCicloAtivoId(cicloEmAndamento.id);
      } catch (e: any) {
        if (!cancelado) setErro(e?.message ?? 'Não foi possível carregar os dados do Maestro Contábil.');
      } finally {
        if (!cancelado) setCarregando(false);
      }
    }

    carregar();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.clienteID, recarregarToken]);

  // Dados do ciclo selecionado — recarregados a cada troca de ciclo ativo.
  useEffect(() => {
    if (!cicloAtivoId) return;
    let cancelado = false;

    async function carregarCiclo() {
      try {
        const [listaAtividades, listaDependencias, listaPdca, listaRanking, prontidao] = await Promise.all([
          maestroService.listarAtividadesPorCiclo(cicloAtivoId as number),
          maestroService.listarDependenciasPorCiclo(cicloAtivoId as number),
          maestroService.listarPdcaPorCiclo(cicloAtivoId as number),
          maestroService.listarRankingPorCiclo(cicloAtivoId as number),
          maestroService.obterProntidaoEncerramento(cicloAtivoId as number).catch(() => ({ pronto: false, pendencias: [] })),
        ]);
        if (cancelado) return;
        setAtividades(listaAtividades);
        setDependencias(listaDependencias);
        setAcoesPDCA(listaPdca);
        setPontuacoes(listaRanking);
        setProntoParaEncerrar(prontidao.pronto);
        setPendenciasEncerramento(prontidao.pendencias);
      } catch (e: any) {
        if (!cancelado) setErro(e?.message ?? 'Não foi possível carregar os dados do ciclo selecionado.');
      }
    }

    carregarCiclo();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cicloAtivoId, recarregarToken]);

  const empresasEscopo = useMemo(() => {
    const ids = new Set(empresaIdsDoEscopo(escopo, empresas, gruposEmpresas));
    return empresas.filter(e => ids.has(e.id));
  }, [escopo, empresas, gruposEmpresas]);

  const idsEmpresasEscopo = useMemo(() => new Set(empresasEscopo.map(e => e.id)), [empresasEscopo]);

  const atividadesEscopo = useMemo(
    () => atividades.filter(a => idsEmpresasEscopo.size === 0 || idsEmpresasEscopo.has(a.empresaId)),
    [atividades, idsEmpresasEscopo]
  );

  const idsAtividadesEscopo = useMemo(() => new Set(atividadesEscopo.map(a => a.id)), [atividadesEscopo]);

  const dependenciasEscopo = useMemo(
    () => dependencias.filter(d => idsAtividadesEscopo.has(d.atividadeOrigemId) || idsAtividadesEscopo.has(d.atividadeDestinoId)),
    [dependencias, idsAtividadesEscopo]
  );

  const recomendacoes = useMemo(() => derivarRecomendacoes(atividadesEscopo), [atividadesEscopo]);
  const destaques = useMemo(() => derivarDestaques(pontuacoes), [pontuacoes]);

  const indicadores = useMemo<IndicadoresMaestro>(() => {
    const tarefas = atividadesEscopo.filter(a => a.nivelHierarquico === 'TAREFA' || a.nivelHierarquico === 'SUBTAREFA');
    const contar = (status: Atividade['status']) => tarefas.filter(a => a.status === status).length;
    return {
      totalAtividades: tarefas.length,
      concluidas: contar('CONCLUIDA'),
      emAndamento: contar('EM_ANDAMENTO'),
      naoIniciadas: contar('NAO_INICIADA'),
      aguardandoDependencia: contar('AGUARDANDO_DEPENDENCIA'),
      aguardandoEvidencia: contar('AGUARDANDO_EVIDENCIA'),
      atrasadas: contar('ATRASADA'),
      progressoRealizado: tarefas.length ? Math.round(tarefas.reduce((soma, a) => soma + a.progresso, 0) / tarefas.length) : 0,
    };
  }, [atividadesEscopo]);

  const getArea = useCallback((id?: number) => areas.find(a => a.id === id), [areas]);
  const getEmpresa = useCallback((id?: number) => empresas.find(e => e.id === id), [empresas]);
  const getUsuario = useCallback((id?: number) => usuarios.find(u => u.id === id), [usuarios]);

  const grupoEconomico = gruposEconomicos.find(g => g.id === escopo.grupoEconomicoId) ?? gruposEconomicos[0] ?? GRUPO_ECONOMICO_VAZIO;
  const cicloEncontrado = ciclos.find(c => c.id === cicloAtivoId) ?? CICLO_VAZIO;
  // "Pronto para encerrar" não existe como status persistido no backend — é derivado em tempo real
  // do checklist de encerramento (RN-39) sempre que o ciclo está em revisão e sem pendências.
  const cicloAtivo: CicloFechamento = cicloEncontrado.status === 'EM_REVISAO' && prontoParaEncerrar
    ? { ...cicloEncontrado, status: 'PRONTO_PARA_ENCERRAR' }
    : cicloEncontrado;
  const ciclosAnteriores = ciclos.filter(c => c.id !== cicloAtivo.id);

  // Cards dos produtos integrados (Motor de Conciliação/Mapa de Resultado/Revisor Contábil/
  // Workflow SOX) — um por empresa do escopo. Cada chamada é melhor-esforço isolada: se um produto
  // não tem dado para aquela empresa/competência (ex.: sem esteira aberta), o card correspondente
  // simplesmente não aparece, em vez de mostrar zero fabricado.
  useEffect(() => {
    if (!cicloAtivo.id || empresasEscopo.length === 0) { setProdutosIntegrados([]); return; }
    let cancelado = false;

    async function carregarProdutos() {
      const porEmpresa = await Promise.all(empresasEscopo.map(async empresa => {
        const [motor, mapa, revisor, sox] = await Promise.all([
          maestroService.obterResumoMotorConciliacao(empresa.id, cicloAtivo.id, cicloAtivo.competencia).catch(() => null),
          maestroService.obterResumoMapaResultados(empresa.id, cicloAtivo.id, cicloAtivo.competencia).catch(() => null),
          maestroService.obterResumoRevisorContabil(empresa.id, cicloAtivo.id, cicloAtivo.competencia).catch(() => null),
          maestroService.obterResumoWorkflowSox(empresa.id, cicloAtivo.id).catch(() => null),
        ]);
        return [motor, mapa, revisor, sox].filter((p): p is ProdutoIntegrado => p !== null);
      }));

      if (!cancelado) setProdutosIntegrados(porEmpresa.flat());
    }

    carregarProdutos();
    return () => { cancelado = true; };
  }, [cicloAtivo.id, cicloAtivo.competencia, empresasEscopo]);

  const value: MaestroContextValue = {
    carregando,
    erro,
    semDadosCadastrados: !carregando && !erro && gruposEconomicos.length === 0,
    escopo,
    setEscopo,
    grupoEconomico,
    gruposEconomicos,
    gruposEmpresas,
    empresas,
    empresasEscopo,
    areas,
    usuarios,
    feriados,
    cicloAtivo,
    ciclosAnteriores,
    setCicloAtivoId,
    pendenciasEncerramento,
    atividades: atividadesEscopo,
    dependencias: dependenciasEscopo,
    produtosIntegrados,
    recomendacoes,
    acoesPDCA,
    pontuacoes,
    destaques,
    indicadores,
    visaoAtiva,
    setVisaoAtiva,
    atividadeSelecionadaId,
    abaDrawerInicial,
    abrirAtividade: (id: number, aba: AbaAtividadeDrawer = 'resumo') => { setAtividadeSelecionadaId(id); setAbaDrawerInicial(aba); },
    fecharAtividade: () => setAtividadeSelecionadaId(null),
    getArea,
    getEmpresa,
    getUsuario,
    recarregar: () => setRecarregarToken(t => t + 1),
  };

  return <MaestroContext.Provider value={value}>{children}</MaestroContext.Provider>;
};

export const useMaestroContext = (): MaestroContextValue => {
  const context = useContext(MaestroContext);
  if (!context) throw new Error('useMaestroContext deve ser usado dentro de <MaestroProvider>');
  return context;
};
